<script setup lang="ts">
import {type RouteParams, RouterView} from 'vue-router'
import {onMounted, onUnmounted, onUpdated, provide, ref} from 'vue'
import {storeToRefs} from "pinia";
import {useAppDataStore} from '@/stores/app-data'
import {useAuthDataStore} from '@/stores/auth-data'
import {useUxData} from "@/stores/ux-data";
import {Notification, NotificationGroup} from "@progress/kendo-vue-notification";
import {Fade} from "@progress/kendo-vue-animation";
import {handleError, setCookie, SAVED_ROUTE_KEY, redirectToSavedRoute} from "@/appdata/globals";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import { useRequestModal } from '@/composables/useRequestModal';
import ChoiceApplicator from '@/components/molecules/swade/ChoiceApplicator.vue';
import EdgePicker from '@/components/molecules/swade/EdgePicker.vue';
import GuidedSetup from '@/components/guided-setup/GuidedSetup.vue';
import NewCharacter from '@/components/characters/modals/NewCharacter.vue';
import type { AppliedConfiguration } from '@/types/items';
import router from "@/router";
import {getAuth, onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink} from '@firebase/auth';
import {useCurrentUser} from "vuefire";
import {cookieName} from "@/api/client.ts";
import {
  installClickTracking, trackEvent, QUICK_CREATE_CAMPAIGN_EVENT, QUICK_CREATE_CAMPAIGN_UPGRADE_EVENT,
} from "@/appdata/analytics";
import {Dropdown, Tooltip} from "bootstrap";
import PremiumFeatureModal from '@/components/account/PremiumFeatureModal.vue';
import {useGuidedSetupStore} from '@/stores/guided-setup-store';

const firebase = getAuth();
const user = useCurrentUser();
const store = useAppDataStore()
const newCharacterModal = ref<InstanceType<typeof NewCharacter> | null>(null)
const premiumFeatureModal = ref<InstanceType<typeof PremiumFeatureModal> | null>(null)
// Views open the one PremiumFeatureModal mounted here by injecting this.
provide('showPremiumFeature', () => premiumFeatureModal.value?.show())
const setupStore = useGuidedSetupStore();
const auth = useAuthDataStore();
const ux = useUxData()
storeToRefs(store);

if (isSignInWithEmailLink(firebase, window.location.href)) {
  // Additional state parameters can also be passed via URL.
  // This can be used to continue the user's intended action before triggering
  // the sign-in operation.
  // Get the email if available. This should be available if the user completes
  // the flow on the same device where they started it.
  let savedAddress = window.localStorage.getItem('emailForSignIn');
  if (!savedAddress) {
    // User opened the link on a different device. To prevent session fixation
    // attacks, ask the user to provide the associated email again. For example:
    savedAddress = window.prompt('Please provide your email for confirmation');
  }

  // The client SDK will parse the code from the link for you.
  signInWithEmailLink(firebase, savedAddress, window.location.href)
      .then((result) => {
        // Clear email from storage.
        window.localStorage.removeItem('emailForSignIn');
        // You can access the new user by importing getAdditionalUserInfo
        // and calling it with result:
        // getAdditionalUserInfo(result)
        // You can access the user's profile via:
        // getAdditionalUserInfo(result)?.profile
        // You can check if the user is new or existing:
        // getAdditionalUserInfo(result)?.isNewUser
      })
      .catch((error) => {
        handleError(error)
        console.error('Magic link authentication failed')
      });
}

onAuthStateChanged(firebase, (user) => {
  console.info("App | onAuthStateChanged")
  if (user) {
    console.debug("App | onAuthStateChanged | user | ", user)
    setCookie(cookieName, user.accessToken)

    if (!auth.authenticated) {
      auth.$patch({ initialized: false })
      auth.authenticate()
          .then(async () => {
            // Redirect away from login before loading app data.
            // Must happen before ux.fetchUxLabels() to prevent Auth.vue
            // from re-mounting in the authenticated layout and racing
            // with .finally() to consume the saved route.
            if (router.currentRoute.value.name === 'login') {
              redirectToSavedRoute();
            }
            await store.setLoading(true);
            return store.initializeAppData(false)
          })
          .then(() => {
            return ux.fetchUxLabels()
          })
          .then(() => {
            if (!auth.user?.subscriber && !auth.user?.supporter && auth.agedAtLeastThirtyDays) {
              store.notifications.callouts.push(ux.labels.messages.upgradeCallToAction)
            }
          })
          .catch((error) => { handleError(error); })
          .finally(() => {
            store.setLoading(false);
            auth.$patch({ initialized: true });
          })
    } else {
      auth.$patch({ initialized: true });
      if (!auth.user?.subscriber && !auth.user?.supporter) {
        store.notifications.callouts.push(ux.labels.messages.upgradeCallToAction)
      }
    }
  }
  else {
    auth.$patch({
      user: null,
      authenticated: false,
      initialized: true,
    })

    const currentRoute = router.currentRoute.value.fullPath;
    console.debug("App | anonymous user", currentRoute, router.currentRoute);
    if (currentRoute && currentRoute !== '/login') {
      sessionStorage.setItem(SAVED_ROUTE_KEY, currentRoute);
    }

    router.push({ name: 'login' })
  }
});

// Logs the clicks analytics.ts lists (navigation, start-flow, new character) to Fathom.
let uninstallClickTracking: (() => void) | null = null

onMounted(async () => {
  uninstallClickTracking = installClickTracking()
  await store.retrieveStoredSettings();
  store.bindTooltips("#navigation-drawer > .nav-items")
  // One debounced observer keeps every tooltip in the app healthy (cleanup orphans +
  // idempotent rebind), so components no longer hand-manage cleanup/bind on update.
  store.installTooltipObserver()
})

onUnmounted(() => {
  uninstallClickTracking?.()
  store.teardownTooltipObserver()
})

onUpdated(() => {
  store.bindTooltips("#navigation-drawer > .nav-items")
  store.persistSettings()
})

const closeNavigation = () => {
  if (store.settings.navigationExpanded) {
    store.collapseNavigation();
  }
}

const navigateTo = (alias: string, params?: RouteParams) => {
  closeNavigation();

  router.push({ name: alias, params: params });
  store.cleanupTooltips();
}

// The Create tooltip sits on the dropdown's wrapper, #quick-create-menu-button, and its
// data-bs-container keeps it inside that wrapper. The wrapper also holds the open menu, so Bootstrap
// counts the pointer (and focus) as still inside it while the menu is in use. Hide it as the menu opens.
const quickCreateIcon = ref<HTMLElement | null>(null)
const hideQuickCreateTooltip = () => {
  if (quickCreateIcon.value) Tooltip.getInstance(quickCreateIcon.value)?.hide()
}

// Bootstrap does not auto-close these menus after an item is clicked here (the dropdown data API
// still opens them), so the top toggle and the nested one are hidden explicitly.
const closeQuickCreate = () => {
  quickCreateIcon.value
      ?.querySelectorAll<HTMLElement>('.quick-create, .dropend > .dropdown-item[data-bs-toggle="dropdown"]')
      .forEach((toggle) => Dropdown.getInstance(toggle)?.hide())
}

const closeMenus = () => {
  closeQuickCreate();
  closeNavigation();
}

const onQuickCreateGuidedClick = () => {
  closeMenus();
  setupStore.startCharacterFlow();
}

const onQuickCreateTraditionalClick = () => {
  closeMenus();
  newCharacterModal.value?.show();
}

const onQuickCreateCampaignClick = () => {
  closeMenus();
  if (auth.canCreateWorld) {
    trackEvent(QUICK_CREATE_CAMPAIGN_EVENT);
    setupStore.startWorldFlow(store.system);
  } else {
    trackEvent(QUICK_CREATE_CAMPAIGN_UPGRADE_EVENT);
    premiumFeatureModal.value?.show();
  }
}

const onLogoutClick = async (event: any) => {
  await firebase.signOut();
  navigateTo('login');
}

const onCalloutDismiss = (index: number) => {
  store.notifications.callouts = store.notifications.callouts.filter((item, idx) => {
    return idx !== index;
  })
}

const onSuccessDismiss = (index: number) => {
  store.notifications.success = store.notifications.success.filter((item, idx) => {
    return idx !== index;
  })
}

const onErrorDismiss = (index: number) => {
  store.notifications.errors = store.notifications.errors.filter((item, idx) => {
    return idx !== index;
  })
}

const onWarningDismiss = (index: number) => {
  store.notifications.warnings = store.notifications.warnings.filter((item, idx) => {
    return idx !== index;
  })
}

const onNoticeDismiss = (index: number) => {
  store.notifications.info = store.notifications.info.filter((item, idx) => {
    return idx !== index;
  })
}

const choiceApplicatorModal = ref<HTMLElement | null>(null);
useRequestModal(choiceApplicatorModal, () => store.choiceApplicator);

const onChoicesConfirmed = (applied: AppliedConfiguration[]) => store.confirmChoices(applied);
const onChoicesCancelled = () => store.cancelChoices();

const edgePickerModal = ref<HTMLElement | null>(null);
useRequestModal(edgePickerModal, () => store.edgePicker);

const onEdgePickConfirmed = (applied: AppliedConfiguration) => store.confirmEdgePick(applied);
const onEdgePickCancelled = () => store.cancelEdgePick();

store.retrieveStoredSettings()

store.$subscribe((mutation: any, state) => {
  if (mutation?.payload?.notifications) {
    if (mutation?.payload?.notifications?.errors) {
      setTimeout(() => {
        store.notifications.errors.shift();
      }, 5000)
    }

    if (mutation?.payload?.notifications?.success) {
      setTimeout(() => {
        store.notifications.success.shift();
      }, 5000)
    }

    if (mutation?.payload?.notifications?.warnings) {
      setTimeout(() => {
        store.notifications.warnings.shift();
      }, 5000)
    }

    if (mutation?.payload?.notifications?.info) {
      setTimeout(() => {
        store.notifications.info.shift();
      }, 5000)
    }
  }
})
</script>

<template>
  <div v-if="auth.user" id="app-view" class="d-flex vh-100 overflow-hidden">
    <details id="navigation-drawer" class="d-print-none" :class="store.settings.navigationExpanded ? 'open' : ''">
      <summary class="nav-items d-flex flex-column flex-fill" @click.stop="() => {return false;}">
        <span id="drawer-title" class="d-flex flex-fill"
              @click.stop="store.toggleNavigation()"
        >
          <span class="logo mx-2">
            <img v-if="store.settings.navigationExpanded" src="/assets/dicey-app-logo.webp" :alt="ux.labels?.brandName" class="img-fluid"/>
            <img v-else src="/assets/dicey-app-icon.webp" :alt="ux.labels?.brandName" class="img-fluid"/>
          </span>
          <span class="icon toggle-app-bar">
            <span class="fa-light fa-chevron-left"></span>
          </span>
        </span>
        <hr class="mb-4 w-100"/>
        <!-- Strip tooltips carry their text in data-bs-original-title, not title: Bootstrap reads a
             title only once, when the tooltip is created (often before the labels load), but reads
             data-bs-original-title each time it shows. A title would also show natively. -->
        <button
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.home.title : undefined"
            type="button"
            class="btn btn-link landing"
            @click="navigateTo('home')"
        >
          <span class="icon">
            <span class="fa-light fa-home"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.home.label }}</label>
          <span v-if="ux.labels?.mainMenu.home.category" class="category" v-html="ux.labels?.mainMenu.home.category"></span>
        </button>
        <div id="quick-create-menu-button" class="dropend"
             ref="quickCreateIcon"
             data-bs-container="#quick-create-menu-button"
             :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
             :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
             :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.create.title : undefined"
        >
          <button
              type="button"
              class="btn btn-link quick-create"
              data-bs-toggle="dropdown"
              :disabled="store.props.edit"
              @click="hideQuickCreateTooltip"
          >
            <span class="icon">
              <span class="fa-light fa-plus-square"></span>
            </span>
            <label class="label">{{ ux.labels?.mainMenu.create.label }}</label>
            <span v-if="ux.labels?.mainMenu.create.category" class="category" v-html="ux.labels?.mainMenu.create.category"></span>
          </button>
          <ul class="dropdown-menu">
            <li>
              <button
                  type="button"
                  class="dropdown-item btn quick-create-guided"
                  @click="onQuickCreateGuidedClick"
              >
                <span class="icon">
                  <span class="fa-light fa-magic-wand-sparkles"></span>
                </span>
                <label class="label">{{ ux.labels?.mainMenu?.create?.characterBuilder?.label ?? 'Character' }}</label>
                <span class="category">{{ ux.labels?.characterCreator?.guidedSetup ?? 'Guided Setup' }}</span>
              </button>
            </li>
            <li>
              <button type="button" class="dropdown-item btn quick-create-traditional" @click="onQuickCreateTraditionalClick">
                <span class="icon">
                <span class="fa-light fa-puzzle-piece"></span>
                </span>
                <label class="label">{{ ux.labels?.mainMenu?.create?.characterBuilder?.label ?? 'Character' }}</label>
                <span class="category">{{ ux.labels?.characterCreator?.traditionalBuilder ?? 'Traditional Builder' }}</span>
              </button>
            </li>
            <li class="dropdown-divider"></li>
            <li>
              <!-- Marked for the upgrade by the rule its click follows (onQuickCreateCampaignClick):
                   a user who can't create a world is shown the upgrade, everyone else a campaign. -->
              <button type="button" class="dropdown-item btn quick-create-campaign" :class="{
                'requires-premium': !auth.canCreateWorld
              }" @click="onQuickCreateCampaignClick">
                <span class="icon">
                  <span class="fa-light fa-globe"></span>
                </span>
                <label class="label">
                  {{ ux.labels?.mainMenu?.create?.children?.worldEditor?.label ?? 'Campaign' }}
                  <small v-if="!auth.canCreateWorld" class="fa-light fa-gem"></small>
                </label>
                <span class="category">{{ ux.labels?.mainMenu?.create?.children?.worldEditor?.tooltip }}</span>
              </button>
            </li>
          </ul>
        </div>
        <button
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.builder.title : undefined"
            type="button"
            class="btn btn-link characters"
            @click="navigateTo('characters', { system: 'swade' })"
        >
          <span class="icon">
            <span class="fa-light fa-person-rays"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.builder.label }}</label>
          <span v-if="ux.labels?.mainMenu.builder.category" class="category" v-html="ux.labels?.mainMenu.builder.category"></span>
        </button>
        <button
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.dataManager.title : undefined"
            type="button"
            class="btn btn-link data-management"
            @click="navigateTo('data-manager', { system: 'swade' })"
        >
          <span class="icon">
            <span class="fa-light fa-database"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.dataManager.label }}</label>
          <span v-if="ux.labels?.mainMenu.dataManager.category" class="category">{{ ux.labels?.mainMenu.dataManager.category }}</span>
        </button>
        <button
            v-if="auth.user"
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.notifications.title : undefined"
            type="button"
            class="btn btn-link inbox"
            @click="navigateTo('inbox')"
        >
          <span class="icon">
            <span class="fa-light fa-bell"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.notifications.label }}</label>
          <span class="category" v-html="ux.labels?.mainMenu.notifications.category"></span>
        </button>

        <button
            v-if="auth.isAdministrator"
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? (ux.labels?.mainMenu?.dashboard?.title ?? 'Dashboard') : undefined"
            type="button"
            class="btn btn-link dashboard"
            @click="navigateTo('admin-dashboard')"
        >
          <span class="icon">
            <span class="fa-light fa-chart-line"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu?.dashboard?.label ?? 'Dashboard' }}</label>
          <span v-if="ux.labels?.mainMenu?.dashboard?.category" class="category">{{ ux.labels?.mainMenu?.dashboard?.category }}</span>
        </button>

        <button
          v-if="auth.user"
          :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
          :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
          :data-bs-original-title="!store.settings.navigationExpanded ? (auth.user?.username ?? ux.labels?.mainMenu.profile.title) : undefined"
          type="button"
          class="btn btn-link profile"
          @click="navigateTo('profile')"
        >
          <span class="icon">
            <span class="fa-light fa-user-circle"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.profile.label }}</label>
          <span class="category" v-html="auth.user?.username ?? ux.labels?.mainMenu.profile.category"></span>
        </button>
        <a
          :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
          :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
          :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.about.title : undefined"
          class="btn btn-link about-us"
          :href="ux.labels?.mainMenu.about.href ?? 'https://dicey.cc/features'"
          target="_blank"
        >
          <span class="icon">
            <span class="fa-light fa-info-circle"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.about.label }}</label>
          <span class="category">{{ ux.labels?.mainMenu.about.category }}</span>
        </a>
        <a
          v-if="ux.labels?.mainMenu?.discord?.href"
          :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
          :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
          :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.discord.title : undefined"
          class="btn btn-link bug-report"
          :href="ux.labels?.mainMenu.discord.href"
          target="_blank"
        >
          <span class="icon">
            <span class="fa-brands fa-discord"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.discord.label }}</label>
          <span class="category">{{ ux.labels?.mainMenu.discord.category }}</span>
        </a>
        <a
          :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
          :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
          :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.bugReport.title : undefined"
          class="btn btn-link bug-report"
          :href="ux.labels?.mainMenu.bugReport.href ?? 'https://dicey.cc/contact-us/report-a-problem'"
          target="_blank"
        >
          <span class="icon">
            <span class="fa-light fa-bug"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.bugReport.label }}</label>
          <span class="category">{{ ux.labels?.mainMenu.bugReport.category }}</span>
        </a>
        <button
            v-if="auth.user"
            :data-bs-toggle="!store.settings.navigationExpanded ? 'tooltip' : undefined"
            :data-bs-placement="!store.settings.navigationExpanded ? 'right' : undefined"
            :data-bs-original-title="!store.settings.navigationExpanded ? ux.labels?.mainMenu.logout.title : undefined"
            type="button"
            class="btn btn-link logout"
            @click="onLogoutClick"
        >
          <span class="icon">
            <span class="fa-light fa-sign-out"></span>
          </span>
          <label class="label">{{ ux.labels?.mainMenu.logout.label }}</label>
          <span v-if="ux.labels?.mainMenu.logout.category" class="category">{{ ux.labels?.mainMenu.logout.category }}</span>
        </button>
      </summary>
    </details>
    <RouterView v-if="(ux.labels ?? null) !== null"/>
    <LoadingIndicator :visible="store.loading"/>
  </div>

  <div v-else-if="auth.initialized" id="app-view" class="d-flex vh-100 overflow-hidden">
    <RouterView/>
  </div>
  <div v-else id="app-view" class="d-flex vh-100 overflow-hidden">
    <LoadingIndicator :visible="true"/>
  </div>

  <NotificationGroup
    :style="{
        top: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        alignItems: 'flex-center',
      }"
  >
    <Fade :appear="store.notifications.callouts.length > 0">
      <Notification
          v-for="(message, index) in store.notifications.callouts"
          :type="{
            style: 'warning',
            icon: true,
          }"
          :closable="true"
          @close="onCalloutDismiss(index)"
      >
        <span v-html="message"></span>
      </Notification>
    </Fade>
  </NotificationGroup>

  <NotificationGroup
      :style="{
        top: '50%',
        right: '1rem',
        transform: 'translateY(-50%)',
        alignItems: 'flex-end',
      }"
  >
    <Fade :appear="store.notifications.success.length > 0">
      <Notification
          v-for="(message, index) in store.notifications.success"
          :type="{
            style: 'success',
            icon: true,
          }"
          :closable="true"
          @close="onSuccessDismiss(index)"
      >
        <span v-html="message"></span>
      </Notification>
    </Fade>
    <Fade :appear="store.notifications.info.length > 0">
      <Notification
          v-for="(message, index) in store.notifications.info"
          :type="{
            style: 'info',
            icon: true,
          }"
          :closable="true"
          @close="onNoticeDismiss(index)"
      >
        <span v-html="message"></span>
      </Notification>
    </Fade>
    <Fade :appear="store.notifications.errors.length > 0">
      <Notification
          v-for="(message, index) in store.notifications.errors"
          :type="{
            style: 'error',
            icon: true,
          }"
          :closable="true"
          @close="onErrorDismiss(index)"
      >
        <span v-html="message"></span>
      </Notification>
    </Fade>
    <Fade :appear="store.notifications.warnings.length > 0">
      <Notification
          v-for="(message, index) in store.notifications.warnings"
          :type="{
            style: 'warning',
            icon: true,
          }"
          :closable="true"
          @close="onWarningDismiss(index)"
      >
        <span v-html="message"></span>
      </Notification>
    </Fade>
  </NotificationGroup>

  <div
    ref="choiceApplicatorModal"
    class="modal fade"
    id="choice-applicator-modal"
    tabindex="-1"
    aria-labelledby="choice-applicator-title"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="choice-applicator-title">{{ store.choiceApplicator?.source.name ?? '' }}</h5>
        </div>
        <div class="modal-body">
          <ChoiceApplicator
            v-if="store.choiceApplicator"
            :source="store.choiceApplicator.source"
            :choice-sets="store.choiceApplicator.choiceSets"
            :existing-choices="store.choiceApplicator.existingChoices"
            @confirmed="onChoicesConfirmed"
            @cancelled="onChoicesCancelled"
          />
        </div>
      </div>
    </div>
  </div>

  <div
    ref="edgePickerModal"
    class="modal fade"
    id="edge-picker-modal"
    tabindex="-1"
    aria-labelledby="edge-picker-title"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="edge-picker-title">{{ store.edgePicker?.source.name ?? '' }}</h5>
        </div>
        <div class="modal-body">
          <EdgePicker
            v-if="store.edgePicker"
            :source="store.edgePicker.source"
            :original-mod="store.edgePicker.originalMod"
            :world="store.edgePicker.world"
            :existing="store.edgePicker.existing"
            @confirmed="onEdgePickConfirmed"
            @cancelled="onEdgePickCancelled"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- The guided setup flows open from Home's Quick Start panel and the Create menu, so their
       fullscreen modal is mounted once here rather than in any one view. The new character
       dialog is mounted here for the same reason: the Create menu and two views open it. -->
  <template v-if="auth.user && (ux.labels ?? null) !== null">
    <GuidedSetup />
    <NewCharacter v-if="store.system" ref="newCharacterModal" :system="store.system" />
    <PremiumFeatureModal ref="premiumFeatureModal" />
  </template>

    <!--
    <div v-if="store.loading && (!user)" class="my-auto mx-auto p-5 w-25 text-warning">
      <p class="text-center">
        <span class="fa-light fa-fw fa-5x fa-loader fa-spin"></span>
      </p>
      <p class="h5 text-center fw-bold text-uppercase">
        {{ ux.labels?.userLoggingIn }}
      </p>
    </div>
    <div v-else class="my-auto mx-auto p-5 w-25 text-warning">
      <p class="text-center">
        <span class="fa-light fa-fw fa-5x fa-exclamation-triangle"></span>
      </p>
      <p class="h5 text-center fw-bold text-uppercase">
        {{ ux.labels?.userDisabled }}
      </p>
    </div>
    -->
</template>
